import { useEffect, useState, useMemo } from "react";
import CustomCard from "../../components/CustomCard/CustomCard";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import TableHeader from "../../components/CustomTable/TableHeader";
import { getEnterpriseView } from "../../redux/slices/enterpriseSlice";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";
import EnterpriseTable from "./Components/EnterpriseTable";
import { capitalizeEntireWord, downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers";
import {
  ENT_STATUS_DISPLAY_NAMES,
  ENT_STATUS,
  TITLE_TXT,
  BTN_TXT,
  ROLE_MODAL_WIDTHS,
} from "../../constants/constants";
import CustomModal from "../../components/CustomModal/CustomModal";
import "./Enterprise.scss";
import NewEntForm from "./Components/NewEntForm";
import { PERM_CODE, ENT_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";
import { count } from "../../redux/slices/dealsSlice";
import { message } from "antd";

const buildStatusTableTitle = ({
  selectedStatus,
  statusDisplayNames = {},
  statusListWithCounts = [],
  defaultTitle = "",
}) => {
  const getCount = (id) => {
    const item = statusListWithCounts?.find((s) => s.statusId === id);
    return Number(item?.count) || 0;
  };

  if (Array.isArray(selectedStatus)) {
    if (selectedStatus.length === 0) {
      return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
    }
    return selectedStatus
      .map((id, index) => {
        const rawName = statusDisplayNames?.[id] || id;
        const name = capitalizeEntireWord(rawName) || rawName || "";
        const count = getCount(id);
        const isLast = index === selectedStatus.length - 1;
        return isLast ? `${name} LIST (${count})` : `${name} (${count})`;
      })
      .join(", ");
  }

  if (selectedStatus) {
    const rawName = statusDisplayNames?.[selectedStatus] || selectedStatus || defaultTitle;
    const name = capitalizeEntireWord(rawName) || rawName || "";
    const count = getCount(selectedStatus);
    return `${name} LIST (${count})`;
  }

  return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
};

const Enterprise = () => {
  const dispatch = useDispatch();
  const { enterpriseList, loading } = useSelector((state) => state?.enterprise);
  const { statusList } = useSelector((state) => state?.enqMngt);
  const { user } = useSelector((state) => state.auth);
  const { loading: docLoading, error: docError } = useSelector((state) => state.documents);
  const { CountData } = useSelector((state) => state?.deals);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [modal, setModal] = useState(false);
  const [columnControl, setColumnControl] = useState(null);

  // Get user's privilege codes
  const userPrivileges = useMemo(
    () => user?.privilege?.map((p) => p.privilegeCode) || [],
    [user]
  );

  // Determine allowed statuses for the user
  const allowedStatusIds = useMemo(
    () => userPrivileges.flatMap((priv) => ENT_STATUS_PRIVILEGE_MAP[priv] || []),
    [userPrivileges]
  );

const DEFAULT_ENT_STATUSES = [
  ENT_STATUS.PENDING.ID,
  ENT_STATUS.ACTIVE.ID,
];

  // Fetch statuses and set default selected
  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("ent"));
      if (res?.payload?.statusCode === 200) {
        const preferredStatuses = DEFAULT_ENT_STATUSES.filter((id) =>
          allowedStatusIds.includes(id),
        );
        setSelectedStatus(
          preferredStatuses.length > 0
            ? preferredStatuses
            : allowedStatusIds[0]
            ? [allowedStatusIds[0]]
            : [],
        );
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds]);

  // Fetch enterprise data
  useEffect(() => {
    if (selectedStatus) {
      dispatch(getEnterpriseView(selectedStatus));
    }
  }, [dispatch, selectedStatus]);

  // Fetch deals count once
  useEffect(() => {
    dispatch(count("ENTERPRISE"));
  }, [dispatch,]);

  // Search hook
  const { searchText, setSearchText, filteredData } =
    useSearchableTable(enterpriseList);

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  // Filter status dropdown by privileges
  const filteredStatusList = statusList?.filter((status) =>
    allowedStatusIds.includes(status.statusId)
  );

  // Merge counts **only for IsDeal statuses**
  const isDealStatusIds = Object.values(ENT_STATUS).map((s) => s.ID);
  const statusListWithCounts = filteredStatusList.map((item) => {
    const isDeal = isDealStatusIds.includes(item.statusId);

    return {
      ...item,
      count: isDeal ? CountData?.find((c) => c.statusId === item.statusId)?.count || 0 : undefined,
    };
  });

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        selectedStatus,
        statusDisplayNames: ENT_STATUS_DISPLAY_NAMES,
        statusListWithCounts,
        defaultTitle: "Enterprise",
      }),
    [selectedStatus, statusListWithCounts],
  );

  // ============================
  // ✅ DOWNLOAD HANDLERS
  // ============================

  const handleDownload = (type) => downloadPageHandle({ dispatch, type, screenName: "enterprise", statusId: selectedStatus });

  useEffect(() => {
    if (docError) {
      message.error(docError || "Download failed");
    }
  }, [docError]);

  return (
    <CustomCard>
      <TableHeader
        title={tableTitle}
        searchText={searchText}
        setSearchText={setSearchText}
        isDropdown={true}
        dropdownData={statusListWithCounts}
        selectedStatus={selectedStatus}
        handleStatusChange={handleStatusChange}
        statusDisplayNames={ENT_STATUS_DISPLAY_NAMES}
        btnName={userPrivileges.includes(PERM_CODE.EEACC) ? BTN_TXT.ADD_NEW_ENTERPRISE : undefined}
        handleBtnOnClick={() => setModal(true)}
        isDownloadRequired={true}
        onDownloadPdf={() => handleDownload("pdf")}     // ✅
        onDownloadExcel={() => handleDownload("excel")} // ✅
        downloadLoading={docLoading}
        columnControl={columnControl}
      />
      <EnterpriseTable
        tableData={filteredData}
        selectedStatus={selectedStatus}
        loading={loading}
        onColumnControlChange={setColumnControl}
      />
      {modal && (
        <CustomModal
          title={TITLE_TXT.ADD_NEW_ENTERPRISE}
          open
          centered
          width={ROLE_MODAL_WIDTHS}
          content={
            <NewEntForm
              onClose={() => setModal(false)}
              selectedStatus={selectedStatus}
            />
          }
        />
      )}
    </CustomCard>
  );
};

export default Enterprise;
